import React from 'react';
import { createRoot } from 'react-dom/client';
import ChatWidget from './ChatWidget';

class EmbeddableChatWidget {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    this.options = {
      agentId: '',
      domain: '',
      themeColor: '',
      accessToken: '',
      useSignedUrl: false,
      title: 'DL Chat Assistant',
      position: 'bottom-right', // bottom-right, bottom-left, top-right, top-left
      ...options
    };
    
    if (!this.container) {
      throw new Error('Container element not found');
    }
    
    this.render();
  }
  
  render() {
    // Create a wrapper div for the widget
    const widgetWrapper = document.createElement('div');
    widgetWrapper.id = 'chat-widget-embed';
    
    // Apply base styles
    widgetWrapper.style.position = 'fixed';
    widgetWrapper.style.zIndex = '999999';
    widgetWrapper.style.fontFamily = "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif";
    widgetWrapper.style.margin = '0';
    widgetWrapper.style.padding = '0';
    
    // Apply positioning dynamically
    this.applyPosition(widgetWrapper, this.options.position);
    
    this.container.appendChild(widgetWrapper);
    
    // Render the React component
    const root = createRoot(widgetWrapper);
    root.render(
      React.createElement(ChatWidget, {
        agentId: this.options.agentId,
        domain: this.options.domain,
        themeColor: this.options.themeColor,
        accessToken: this.options.accessToken,
        useSignedUrl: this.options.useSignedUrl,
        title: this.options.title,
        embedded: true
      })
    );
    
    this.root = root;
    this.widgetWrapper = widgetWrapper; // Store reference for position updates
  }
  
  // Method to apply positioning dynamically
  applyPosition(wrapper, position) {
    // Reset all positioning
    wrapper.style.top = 'auto';
    wrapper.style.bottom = 'auto';
    wrapper.style.left = 'auto';
    wrapper.style.right = 'auto';
    
    // Apply new positioning
    switch (position) {
      case 'bottom-right':
        wrapper.style.bottom = '20px';
        wrapper.style.right = '20px';
        break;
      case 'bottom-left':
        wrapper.style.bottom = '20px';
        wrapper.style.left = '20px';
        break;
      case 'top-right':
        wrapper.style.top = '20px';
        wrapper.style.right = '20px';
        break;
      case 'top-left':
        wrapper.style.top = '20px';
        wrapper.style.left = '20px';
        break;
      default:
        wrapper.style.bottom = '20px';
        wrapper.style.right = '20px';
    }
  }
  
  // Method to update position without re-rendering
  updatePosition(newPosition) {
    if (this.widgetWrapper) {
      this.applyPosition(this.widgetWrapper, newPosition);
      this.options.position = newPosition;
    }
  }
  
  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.container && this.container.querySelector('#chat-widget-embed')) {
      this.container.querySelector('#chat-widget-embed').remove();
    }
  }

  // Method to update options
  updateOptions(newOptions) {
    const oldPosition = this.options.position;
    this.options = { ...this.options, ...newOptions };
    
    // If only position changed, use the optimized updatePosition method
    if (newOptions.position && newOptions.position !== oldPosition) {
      this.updatePosition(newOptions.position);
    } else {
      // For other option changes, re-render
      this.destroy();
      this.render();
    }
  }
}

// Export for webpack - this is the main export
export default EmbeddableChatWidget;

// Make it available globally for immediate use - multiple ways to ensure it works
if (typeof window !== 'undefined') {
  window.ChatWidget = EmbeddableChatWidget;
  window.EmbeddableChatWidget = EmbeddableChatWidget;
}

// Also try to set it on the global object
if (typeof global !== 'undefined') {
  global.ChatWidget = EmbeddableChatWidget;
}

// Fallback: try to set it on 'this' if available
if (typeof this !== 'undefined') {
  this.ChatWidget = EmbeddableChatWidget;
}

// Final fallback: try to set it on the root object
if (typeof root !== 'undefined') {
  root.ChatWidget = EmbeddableChatWidget;
}
